import { useState, useEffect } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { zodValidator, fallback } from '@tanstack/zod-adapter'
import { Plus, Search, Package } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { knowledgePackQueries } from '@/lib/api/queries/knowledge-pack'
import { packStatusFilterOptions } from '@/lib/constants/knowledge-pack'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { PackStatCards } from '@/components/knowledge-packs/pack-stat-cards'
import { PackListSkeleton } from '@/components/knowledge-packs/pack-list-skeleton'
import { PackCard } from '@/components/knowledge-packs/pack-card'
import { CreatePackDialog } from '@/components/knowledge-packs/create-pack-dialog'
import { PaginationControls } from '@/components/agents/pagination-controls'
import { PackDrawer } from '@/components/knowledge-packs/pack-drawer'
import { useDeleteKnowledgePack } from '@/lib/api/mutations/knowledge-pack'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { KnowledgePack } from '@/lib/api/types'
import { usePermissions } from '@/lib/hooks/use-permissions'

const packSearchSchema = z.object({
  search: fallback(z.string(), '').default(''),
  status: fallback(z.string(), '').default(''),
  page: fallback(z.number(), 1).default(1),
  pageSize: fallback(z.number(), 20).default(20),
  sortBy: fallback(z.string(), 'createdAt').default('createdAt'),
  sortOrder: fallback(z.string(), 'desc').default('desc'),
})

export type PackSearch = z.infer<typeof packSearchSchema>

export const Route = createFileRoute('/_authenticated/knowledge-packs')({
  validateSearch: zodValidator(packSearchSchema),
  component: KnowledgePacksPage,
})

function KnowledgePacksPage() {
  const { canCreateKnowledgePack, canDeleteKnowledgePack, canEditKnowledgePack } = usePermissions()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  // Local search state for immediate typing response
  const [searchInput, setSearchInput] = useState(search.search)
  const debouncedSearch = useDebounce(searchInput, 300)

  // Dialog and selection state
  const [selectedPackId, setSelectedPackId] = useState<string | null>(null)
  const [editModeForDrawer, setEditModeForDrawer] = useState(false)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    name: string
  } | null>(null)

  const deleteMutation = useDeleteKnowledgePack()

  // Sync debounced search to URL params
  useEffect(() => {
    if (debouncedSearch !== search.search) {
      setFilters({ search: debouncedSearch, page: 1 })
    }
  }, [debouncedSearch]) // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize local state from URL on mount/navigation
  useEffect(() => {
    setSearchInput(search.search)
  }, [search.search])

  // Build query params -- strip empty strings and _all sentinel
  const queryParams = {
    search: search.search || undefined,
    status:
      search.status && search.status !== '_all' ? search.status : undefined,
    page: search.page,
    pageSize: search.pageSize,
    sortBy: search.sortBy,
    sortOrder: search.sortOrder,
  }

  const packsQuery = useQuery(knowledgePackQueries.list(queryParams))

  const setFilters = (updates: Record<string, string | number>) => {
    navigate({
      search: (prev: PackSearch) => {
        const next = { ...prev }
        for (const [key, value] of Object.entries(updates)) {
          ;(next as Record<string, unknown>)[key] =
            value === '_all' ? '' : value
        }
        return next
      },
    })
  }

  const packs = packsQuery.data?.data ?? []
  const pagination = packsQuery.data?.pagination
  const totalPages = pagination?.totalPages ?? 0
  const hasFilters = !!(search.search || search.status)

  const handlePackCreated = (pack: KnowledgePack) => {
    setSelectedPackId(pack.id)
    setEditModeForDrawer(false)
    setCreateDialogOpen(false)
  }

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        if (selectedPackId === deleteTarget.id) {
          setSelectedPackId(null)
        }
        setDeleteTarget(null)
      },
    })
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-4xl font-medium font-serif text-foreground">
            Knowledge Packs
          </h1>
          <p className="text-base text-muted-foreground mt-1">
            Organize and manage your document collections
          </p>
        </div>
        {canCreateKnowledgePack && (
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="w-5 h-5 mr-2" />
            Create Pack
          </Button>
        )}
      </div>

      {/* Search + Filter Row */}
      <div className="flex items-center gap-4 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search knowledge packs..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-12 pr-4 py-3"
          />
        </div>
        <Select
          value={search.status || '_all'}
          onValueChange={(value) =>
            setFilters({ status: value === '_all' ? '' : value, page: 1 })
          }
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            {packStatusFilterOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stat Cards */}
      <PackStatCards />

      {/* Pack List or Loading/Empty State */}
      {packsQuery.isPending ? (
        <PackListSkeleton />
      ) : packs.length === 0 ? (
        hasFilters ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Package className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-base font-medium text-foreground mb-1">
              No packs found
            </h3>
            <p className="text-sm text-muted-foreground">
              Try adjusting your search or filters.
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <Package className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-base font-medium text-foreground mb-1">
              No knowledge packs yet
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create your first knowledge pack to organize documents.
            </p>
            {canCreateKnowledgePack && (
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Pack
              </Button>
            )}
          </div>
        )
      ) : (
        <div className="space-y-4">
          {packs.map((pack) => (
            <PackCard
              key={pack.id}
              pack={pack}
              onClick={() => {
                setSelectedPackId(pack.id)
                setEditModeForDrawer(false)
              }}
              onEdit={canEditKnowledgePack ? () => {
                setSelectedPackId(pack.id)
                setEditModeForDrawer(true)
              } : undefined}
              onDelete={canDeleteKnowledgePack ? () =>
                setDeleteTarget({ id: pack.id, name: pack.name })
              : undefined}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationControls
          page={search.page}
          totalPages={totalPages}
          onPageChange={(page) => setFilters({ page })}
        />
      )}

      {/* Create Pack Dialog */}
      <CreatePackDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={handlePackCreated}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteTarget?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this knowledge pack and all its
              files. Linked agents will lose access to this knowledge source.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Pack</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Pack'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pack Side Drawer */}
      {selectedPackId && (
        <PackDrawer
          packId={selectedPackId}
          open={!!selectedPackId}
          onOpenChange={(open) => {
            if (!open) setSelectedPackId(null)
          }}
          editMode={editModeForDrawer}
        />
      )}
    </div>
  )
}
